import { DOCUMENT } from '@angular/common';
import {
    Component, Inject, OnDestroy, OnInit, Signal, input, output,
} from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { MdIconComponent } from '@inditex/mduikit';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { filter, Subject, takeUntil } from 'rxjs';

import { ProductColorSelectorComponent } from '../product-color-selector/product-color-selector.component';
import { ProductPricesComponent } from '../product-prices/product-prices.component';
import { ProductSizeSelectorLayoutComponent } from '../product-size-selector';
import { ProductViewService } from '../product-view/services/product-view.service';

import { AddToCartSheetModel, hasScroll, isPdpExtraInfoOpen } from './add-to-cart-bottom-sheet.models';

import { CorePlatformService } from '@core/infrastructure/platform/platform.service';
import { getCmsConfigValue, getIsOpenForSale } from '@core/infrastructure/state/core.selectors';
import { SlideElementsComponent } from '@features/cms-collection';
import { MediasPublicConstants, MediasPublicModels } from '@features/products/medias';
import { ProductPublicFunctions, ProductPublicModels } from '@features/products/product';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ViewItemService } from '@shared/analytics/COMMON/services/product-view/view-item.service';
import { ProductMediaSkeletonComponent } from '@shared/media';
import { GoToProduct } from '@shared/navigation';
import { ProductMapPrices } from '@shared/prices/models/product-prices.model';
import { getPricesProductSyncUseCase } from '@shared/prices/use-cases/get-prices.use-cases';
import { ProductMeasurementLabelUseCase, openSizeGuideUseCase } from '@shared/product/';
import { SizeGuideComponent } from '@shared/product/components/app-size-guide/app-size-guide.component';
import { FitSizeMessageComponent } from '@shared/product/components/product-fit-size-and-mesurament-label/product-fit-size-message.component';
import { ProductViewConfig } from '@shared/product/models';
import { calculateMeasurementLabelUseCase } from '@shared/product/use-cases/product-detail-product.use-case';
import { openNoStocksModalsUseCase } from '@shared/product/use-cases/product-modals.use-cases';
import { getPendingGetCartCore, getShopCartCoreItems } from '@shared/shopcart-core/infrastructure/state/shopcart-core.selectors';

@Component({
    selector: 'add-to-cart-bottom-sheet',
    templateUrl: './add-to-cart-bottom-sheet.component.html',
    standalone: true,
    imports: [
        TranslateModule,
        SizeGuideComponent,
        FitSizeMessageComponent,
        ProductSizeSelectorLayoutComponent,
        ProductColorSelectorComponent,
        SlideElementsComponent,
        MdIconComponent,
        ProductMediaSkeletonComponent,
        ProductPricesComponent,
    ],
    providers: [
        ProductViewService, // dependencia para agregar productos a cesta con redirección para no stores de no venta
        ViewItemService, // dependencia de ProductViewService
    ],
})

export class AddToCartBottomSheetComponent implements OnDestroy, OnInit {
    public sheetModel = input<AddToCartSheetModel>();

    public productViewConfig = input.required<ProductViewConfig>();

    public readonly closeSheet = output<Event>();

    public readonly changeColorAction = output<ProductPublicModels.PublicProductColor>();

    private readonly onDestroy$ = new Subject();

    public showColors: boolean;

    public mocacoRepeated: boolean;

    public productSheet$$: Signal<ProductPublicModels.PublicProduct>;

    public vm: {
        product: ProductPublicModels.PublicProduct;
        showColors: boolean;
        hasPromotion: boolean;
        measurementModel: ProductMeasurementLabelUseCase;
        showMeasurement: boolean;
        showSizes: boolean;
        images: MediasPublicModels.PublicMediaResolved[];
        priceInfoView: ProductMapPrices;
    };

    constructor(
        private readonly store: Store,
        private readonly router: Router,
        @Inject(DOCUMENT) private readonly document: Document,
        private readonly productViewService: ProductViewService,
        private readonly platformService: CorePlatformService
    ) {
        this.router.events.pipe(
            filter((event) => event instanceof NavigationStart && event.navigationTrigger === 'popstate'),
            takeUntil(this.onDestroy$),
        ).subscribe(() => {
            this.closeButtonSheet({});
        });
    }

    public ngOnInit(): void {
        if (this.platformService.isBrowser()) {
            this.document.body.classList.remove(hasScroll);
            this.document.body.classList.add(isPdpExtraInfoOpen);
        }

        this.vm = this.setViewModel(this.sheetModel().product);
        this.mocacoRepeated = this.isMocacoRepeated();
    }

    public closeButtonSheet($event): void {
        if (this.platformService.isBrowser()) {
            this.document.body.classList.add(hasScroll);
            this.document.body.classList.remove(isPdpExtraInfoOpen);
        }

        this.closeSheet.emit($event);
    }

    public goToProduct($event): void {
        $event?.preventDefault();
        this.closeButtonSheet({});

        const data = {
            productId: this.sheetModel().product.id,
            categoryId: this.sheetModel().categoryId,
            colorId: this.sheetModel().product.status.selectedColor.id,
        };

        this.store.dispatch(GoToProduct(data));
    }

    public checkStock(selectedSize) {
        this.closeButtonSheet({});
        const product = this.addSelectedSize(this.vm.product, this.vm.product.status.selectedColor, selectedSize);

        openNoStocksModalsUseCase(this.store, product, EventFrom.productdetail);
    }

    public sizeSelected(selectedSize) {
        this.closeButtonSheet({});

        const product = this.addSelectedSize(this.vm.product, this.vm.product.status.selectedColor, selectedSize);

        // esto debería ser un caso de uso expuesto de forma generica por product
        this.productViewService.actionAddToCartProductView(product, this.productViewConfig(), null);

        this.vm = this.setViewModel(product);
    }

    public openSizeGuide(selectedSize): void {
        const product = this.addSelectedSize(this.vm.product, this.vm.product.status.selectedColor, selectedSize);

        openSizeGuideUseCase(this.store, product, this.sheetModel()?.categoryId);
    }

    public changeColor(selectedColor) {
        // this.closeButtonSheet({}); El cambio de color puede cambiar el precio Negocio debe decidir si cerramos o no al cambio de color
        const product = this.addSelectedSize(this.vm.product, selectedColor, null);

        this.changeColorAction.emit(selectedColor);

        this.vm = this.setViewModel(product);
    }

    public ngOnDestroy(): void {
        this.closeButtonSheet({});
        this.onDestroy$.next(null);
        this.onDestroy$.complete();
    }

    public setViewModel(product) {
        const selectedColor = product?.status?.selectedColor;
        if (!selectedColor) {
            return null;
        }
        const measurementModel = calculateMeasurementLabelUseCase(product);

        const vm = {
            product,
            showSizes: true,
            showColors: product.colors?.length > 1,
            hasPromotion: selectedColor?.sizes?.some((size) => !!size.promotionId),
            measurementModel,
            showMeasurement: measurementModel.measurementLabel.length > 0,
            images: ProductPublicFunctions.GetImagesForColor(
                selectedColor,
                MediasPublicConstants.PublicMediaLocationType.product_page,
            ),
            priceInfoView: getPricesProductSyncUseCase(this.store, product),
        };
        return vm;
    }

    private addSelectedSize(product, selectedColor, selectedSize) {
        return {
            ...product,
            priceInfo: selectedSize?.priceInfo ?? selectedColor?.sizes[0].priceInfo,
            status: {
                ...product.status,
                selectedColor,
                selectedSize,
            },
        };
    }

    private isMocacoRepeated(): boolean {
        const sastreActivated$$ = this.store.selectSignal(getCmsConfigValue('sastre_activate'));
        const isOpenForSale$$ = this.store.selectSignal(getIsOpenForSale);

        // biome-ignore lint/complexity/useSimplifiedLogicExpression: <explanation>
        if (!sastreActivated$$() || !isOpenForSale$$()) return false;

        const selectedColor = this.sheetModel().product.status.selectedColor;

        if (selectedColor?.uniqueSize) return false;

        const shopcartItems$$ = this.store.selectSignal(getShopCartCoreItems)();
        const pendingCart$$ = this.store.selectSignal(getPendingGetCartCore)();
        const allCartItems = [...shopcartItems$$ || [], ...pendingCart$$ || []];
        const selectedColorSizes = selectedColor.sizes;

        // Verificamos si algún SKU de `product` está en la cesta
        return selectedColorSizes.some(size => allCartItems?.some(item => item.sku === size.sku));
    }
}
